import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import classes from './DealerStockDetailContent.module.scss';
import Loading from '../Layout/Loading';
import EmptyContentCard from '../Layout/EmptyContentCard';
import DealerStockCard from './DealerStockCard';
import DealerFavoriteButton from './DealerFavoriteButton';
import DealerInquiryForm from './DealerInquiryForm';
import placeholderImage from '../../media/images/emptyFavorites.png';
import { buildVehicleSlug, buildVehicleTitle, getChassisCode } from '../../helper/vehicleTitle';
import {
    formatMileage,
    formatTransmission,
    formatBodyType,
    formatColor,
    saleStateFor,
} from '../../helper/dealerStockFilters';
import {
    fetchDealerStockById,
    fetchSimilarDealerStock,
    galleryImagesForCar,
    priceForCar,
    shippedPriceForCar,
    quoteForCar,
    US_PORTS,
    TITLE_FEE,
    formatUsd,
} from '../../services/dealerStock';
import { trackDealerView } from '../../services/dealerAnalytics';

// The middle question differs by source: a car still in Japan is quoted a
// shipped price, while curated inventory has already landed and has none — so
// asking "what does the Shipped Price include?" on a car with no shipped price
// would answer a question the page never raises.
const faqsFor = (isCurrentInventory) => [
    {
        q: 'How long does importing take?',
        a: isCurrentInventory
            ? 'This car is already in the United States, so there is no import wait — only the paperwork and delivery time from our lot to you. Vehicles we source to order from Japan typically take 6-10 weeks.'
            : 'Most vehicles arrive at a US port 6-10 weeks after purchase. Deregistration and booking in Japan take about two weeks, ocean freight is roughly three to five weeks depending on the destination port, and customs clearance adds a few days.',
    },
    isCurrentInventory
        ? {
            q: 'What does the price include?',
            a: 'The listed price is for the vehicle as it sits in our US inventory, already imported and customs-cleared. It does not include state sales tax, title and registration, or delivery from our location to you.',
        }
        : {
            q: 'What does the Shipped Price include?',
            a: 'Ocean freight for this body type, inland transport to the port in Japan, Japanese export clearance, and our service fee. It does not include US customs duty, port fees at arrival, or domestic delivery from the port to you.',
        },
    {
        q: 'Is this vehicle US import eligible?',
        a: isCurrentInventory
            ? 'This vehicle is already imported and US-titled, so eligibility is settled. For cars we source from Japan, anything 25 years or older imports freely under the FMVSS and EPA exemption.'
            : 'Vehicles 25 years or older are exempt from FMVSS and EPA conformity requirements and import freely. Newer vehicles are possible in some cases - ask us before committing.',
    },
];

const DealerStockDetailContent = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(true);

    const [activeImage, setActiveImage] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);

    // Quote panel. Mirrors the marketing site's "Get a Quote" form: two price
    // tiers and an imported-only title add-on.
    const [showQuote, setShowQuote] = useState(false);
    const [quoteType, setQuoteType] = useState('shipped');
    const [includeTitle, setIncludeTitle] = useState(false);
    const [port, setPort] = useState('');

    const [similar, setSimilar] = useState([]);
    const [similarLoading, setSimilarLoading] = useState(true);

    // Which in-page request form is open: 'inquiry' | 'quote' | 'inspection'.
    // These used to link out to the marketing site's contact page; they now
    // post to the API so the request is tied to the member's account and shows
    // in their garage on both sites.
    const [inquiryMode, setInquiryMode] = useState(null);
    const inquiryRef = useRef(null);

    // Focus bookkeeping for the lightbox. It declares aria-modal, which promises
    // assistive tech that everything outside it is unreachable — so focus has to
    // actually move into it, stay inside it, and go back where it came from.
    const lightboxRef = useRef(null);
    const lightboxOpenerRef = useRef(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setActiveImage(0);
        setLightboxOpen(false);
        setShowQuote(false);
        setQuoteType('shipped');
        setIncludeTitle(false);
        setPort('');
        setInquiryMode(null);

        fetchDealerStockById(id)
            .then((data) => {
                if (!active) return;
                setVehicle(data);
                // A different id back means this one was retired as a duplicate
                // and that is the copy the scraper kept. Move to its page, the
                // same place the marketing site's 301 sends this link.
                if (data?._id && String(data._id) !== String(id)) {
                    navigate(`/dealer-stock/${buildVehicleSlug(data)}/${data._id}`, { replace: true });
                }
            })
            .finally(() => active && setLoading(false));

        return () => { active = false; };
    }, [id, navigate]);

    // One view per car page for the dealer analytics (the API also drops a
    // repeat within half an hour, so a re-render or a reload costs nothing).
    useEffect(() => {
        if (vehicle && String(vehicle._id) === String(id)) trackDealerView(vehicle, priceForCar(vehicle));
    }, [vehicle, id]);

    useEffect(() => {
        let active = true;
        setSimilarLoading(true);
        fetchSimilarDealerStock(id, 6)
            .then((cars) => active && setSimilar(cars.filter((c) => c._id !== id)))
            .finally(() => active && setSimilarLoading(false));
        return () => { active = false; };
    }, [id]);

    useEffect(() => {
        if (inquiryMode && inquiryRef.current) {
            inquiryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [inquiryMode]);

    const openRequest = (mode) => setInquiryMode((current) => (current === mode ? null : mode));

    const images = useMemo(() => {
        const gallery = galleryImagesForCar(vehicle);
        return gallery.length ? gallery : [placeholderImage];
    }, [vehicle]);

    const goToNext = useCallback(
        () => setActiveImage((i) => (i + 1) % images.length),
        [images.length]
    );
    const goToPrevious = useCallback(
        () => setActiveImage((i) => (i - 1 + images.length) % images.length),
        [images.length]
    );

    // Arrow keys page the gallery, Escape closes it, and Tab cycles within it,
    // so the lightbox behaves like every other image viewer.
    useEffect(() => {
        if (!lightboxOpen) return undefined;

        const overlay = lightboxRef.current;
        // Captured now, not read in cleanup: by the time cleanup runs the ref
        // may already point somewhere else (or nowhere).
        const opener = lightboxOpenerRef.current;
        const focusables = () =>
            overlay ? [...overlay.querySelectorAll('button')].filter((el) => !el.disabled) : [];

        // Move focus in. Without this the focus ring stays on the hero button
        // behind the overlay, and the first Tab walks into the page underneath.
        const first = focusables()[0];
        if (first) first.focus();

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setLightboxOpen(false);
                return;
            }
            if (e.key === 'ArrowRight') {
                goToNext();
                return;
            }
            if (e.key === 'ArrowLeft') {
                goToPrevious();
                return;
            }
            if (e.key !== 'Tab') return;

            // Trap: wrap from the last control back to the first and vice versa.
            const items = focusables();
            if (items.length === 0) return;
            const firstItem = items[0];
            const lastItem = items[items.length - 1];
            if (e.shiftKey && document.activeElement === firstItem) {
                e.preventDefault();
                lastItem.focus();
            } else if (!e.shiftKey && document.activeElement === lastItem) {
                e.preventDefault();
                firstItem.focus();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        // Stop the page behind the overlay from scrolling under it. BODY is the
        // scrolling element in this app (see index.scss), so this is the one
        // that matters.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
            // Hand focus back to whatever opened the gallery.
            if (opener) opener.focus();
        };
    }, [lightboxOpen, goToNext, goToPrevious]);

    if (loading) {
        return (
            <div className={classes.stateContainer}>
                <Loading text="Vehicle" />
            </div>
        );
    }

    if (!vehicle) {
        return (
            <div className={classes.stateContainer}>
                <EmptyContentCard
                    header="Vehicle not found"
                    message="This dealer stock listing is no longer available."
                />
                <Link to="/dealer-stock" className={classes.backLink}>
                    <i className="fas fa-arrow-left" aria-hidden="true"></i> Back to Dealer Stock
                </Link>
            </div>
        );
    }

    // buildVehicleTitle folds the chassis code and trim into the name, so this
    // reads "1995 Nissan Skyline R33 GT-R V-Spec" — the same string the
    // marketing site prints for the same listing.
    const title = buildVehicleTitle(vehicle);

    // Curated inventory is ALREADY LANDED and priced in USD. Adding Japan-to-US
    // freight to it would invent roughly $3,350 of shipping the buyer will never
    // pay, so the shipped-price block is suppressed for that source entirely —
    // which is what the marketing site does too.
    const isCurrentInventory = vehicle.source === 'current-inventory';

    const price = priceForCar(vehicle);
    const shipped = isCurrentInventory ? 0 : shippedPriceForCar(vehicle);
    const quote = quoteForCar(vehicle, { quoteType, includeTitle });
    const shortName = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');

    const { sold, badge } = saleStateFor(vehicle);
    const chassis = getChassisCode(vehicle);
    const faqs = faqsFor(isCurrentInventory);

    const description = vehicle.grade || vehicle.description || '';

    // A scraped listing's `grade` is a comma/、-separated equipment list
    // ("GT-R V-Spec, HKS Turbo, RAYS 17-inch Alloy Wheels"), which reads far
    // better as bullets than as one run-on line.
    //
    // Curated inventory is the opposite: normalizeInventoryItem copies the
    // admin-written PROSE description into `grade`, and splitting prose on its
    // commas shreds sentences into fragments. So only scraped listings get the
    // bullet treatment, and only when the split actually yields a list of short
    // items rather than a couple of long clauses.
    const equipmentList = (() => {
        if (isCurrentInventory || !description) return null;
        const parts = description.split(/[、,]\s*/).map((part) => part.trim()).filter(Boolean);
        const looksLikeSpecList = parts.length > 1 && parts.every((part) => part.length <= 60);
        return looksLikeSpecList ? parts : null;
    })();

    const specs = [
        { icon: 'far fa-calendar', label: 'Year', value: vehicle.year },
        { icon: 'fas fa-industry', label: 'Make', value: vehicle.make },
        { icon: 'fas fa-car', label: 'Model', value: vehicle.model },
        { icon: 'fas fa-road', label: 'Mileage', value: formatMileage(vehicle) },
        { icon: 'fas fa-cog', label: 'Transmission', value: formatTransmission(vehicle) },
        { icon: 'fas fa-palette', label: 'Color', value: formatColor(vehicle) },
        { icon: 'fas fa-car-side', label: 'Body Type', value: formatBodyType(vehicle) },
        { icon: 'fas fa-wrench', label: 'Chassis', value: chassis },
        { icon: 'fas fa-history', label: 'Repair History', value: vehicle.repairHistory },
    ].map((s) => ({ ...s, value: s.value || 'N/A' }));

    return (
        <div className={classes.detail}>
            <nav className={classes.breadcrumbs} aria-label="Breadcrumb">
                <Link to="/">Home</Link>
                <span aria-hidden="true">/</span>
                <Link to="/dealer-stock">Dealer Stock</Link>
                {vehicle.make && (
                    <>
                        <span aria-hidden="true">/</span>
                        <Link to={`/dealer-stock?make=${encodeURIComponent(vehicle.make)}`}>{vehicle.make}</Link>
                    </>
                )}
                {vehicle.make && vehicle.model && (
                    <>
                        <span aria-hidden="true">/</span>
                        <Link
                            to={`/dealer-stock?make=${encodeURIComponent(vehicle.make)}&model=${encodeURIComponent(vehicle.model)}`}
                        >
                            {vehicle.model}
                        </Link>
                    </>
                )}
                <span aria-hidden="true">/</span>
                <span className={classes.breadcrumbCurrent}>{title}</span>
            </nav>

            {sold && (
                <div className={classes.soldBanner}>
                    <i className="fas fa-circle-exclamation" aria-hidden="true"></i>
                    This vehicle has been sold. Contact us and we&rsquo;ll source another like it.
                </div>
            )}

            <div className={classes.layout}>
                {/* ---------------- Gallery ---------------- */}
                <div className={classes.gallery}>
                    <button
                        type="button"
                        className={classes.mainImage}
                        ref={lightboxOpenerRef}
                        onClick={() => setLightboxOpen(true)}
                        aria-label="View larger image"
                    >
                        <img
                            src={images[activeImage] || placeholderImage}
                            alt={title}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = placeholderImage;
                            }}
                        />
                        <span className={classes.zoomIcon}>
                            <i className="fas fa-search-plus" aria-hidden="true"></i> View Larger
                        </span>
                        {images.length > 1 && (
                            <span className={classes.imageCounter}>
                                {activeImage + 1} / {images.length}
                            </span>
                        )}
                    </button>

                    {images.length > 1 && (
                        <>
                            <button
                                type="button"
                                className={`${classes.heroNav} ${classes.heroNavPrev}`}
                                onClick={goToPrevious}
                                aria-label="Previous image"
                            >
                                <i className="fas fa-chevron-left" aria-hidden="true"></i>
                            </button>
                            <button
                                type="button"
                                className={`${classes.heroNav} ${classes.heroNavNext}`}
                                onClick={goToNext}
                                aria-label="Next image"
                            >
                                <i className="fas fa-chevron-right" aria-hidden="true"></i>
                            </button>

                            <div className={classes.thumbnails}>
                                {images.map((img, i) => (
                                    <button
                                        key={`${img}-${i}`}
                                        type="button"
                                        className={`${classes.thumb} ${i === activeImage ? classes.activeThumb : ''}`}
                                        onClick={() => setActiveImage(i)}
                                        aria-label={`View image ${i + 1} of ${images.length}`}
                                    >
                                        <img
                                            src={img}
                                            alt={`${title} ${i + 1}`}
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = placeholderImage;
                                            }}
                                        />
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* ---------------- Summary ---------------- */}
                <div className={classes.summary}>
                    <div className={classes.titleRow}>
                        <h1 className={classes.title}>{title}</h1>
                        {badge && (
                            <span className={`${classes.statusBadge} ${classes[`status${badge}`]}`}>{badge}</span>
                        )}
                        <DealerFavoriteButton car={vehicle} size="lg" showLabel className={classes.saveButton} />
                    </div>

                    {/* Scraped listings only. For curated inventory `grade` is a
                        copy of the prose description (see normalizeInventoryItem),
                        so printing it here would dump the whole write-up under the
                        H1 and then repeat it in the description section below. */}
                    {!isCurrentInventory && vehicle.grade && (
                        <p className={classes.gradeLine}>{vehicle.grade}</p>
                    )}

                    <div className={classes.priceBlock}>
                        <span className={classes.priceLabel}>Price</span>
                        <div className={classes.price}>{formatUsd(price)}</div>

                        {isCurrentInventory ? (
                            <p className={classes.landedNote}>
                                <i className="fas fa-check-circle" aria-hidden="true"></i>
                                Already imported and in our US inventory — no freight to add.
                            </p>
                        ) : (
                            <div className={classes.shippedPrice}>
                                <span className={classes.shippedLabel}>Shipped Price</span>
                                <div className={classes.shippedValue}>
                                    {shipped ? formatUsd(shipped) : 'ASK'}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={classes.specs}>
                        {specs.map((s) => (
                            <div key={s.label} className={classes.specItem}>
                                <i className={s.icon} aria-hidden="true"></i>
                                <div>
                                    <span className={classes.specLabel}>{s.label}</span>
                                    <span className={classes.specValue}>{s.value}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {isCurrentInventory && Array.isArray(vehicle.features) && vehicle.features.length > 0 && (
                        <div className={classes.featureList}>
                            {/* h2, not h3: this sits before the first section
                                heading in DOM order, so an h3 here made the
                                outline jump h1 -> h3 -> h2. */}
                            <h2>Features</h2>
                            <div className={classes.featurePills}>
                                {/* Keyed by index: `features` is free-text admin
                                    input and the same value can appear twice in
                                    one vehicle, which duplicate keys would warn
                                    on. The list is static, so index is stable. */}
                                {vehicle.features.map((feature, i) => (
                                    <span key={`${feature}-${i}`} className={classes.featurePill}>{feature}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className={classes.ctaRow}>
                        <button
                            type="button"
                            className={classes.cta}
                            onClick={() => openRequest('inquiry')}
                            aria-expanded={inquiryMode === 'inquiry'}
                        >
                            {inquiryMode === 'inquiry' ? 'Hide Inquiry Form' : 'Inquire About This Vehicle'}
                        </button>
                        {/* Curated inventory has already landed, so there is no
                            shipped/imported choice to make, nothing to quote and
                            nothing left in Japan to inspect. */}
                        {!isCurrentInventory && (
                            <>
                                <button
                                    type="button"
                                    className={classes.ctaSecondary}
                                    onClick={() => setShowQuote((v) => !v)}
                                    aria-expanded={showQuote}
                                >
                                    {showQuote ? 'Hide Quote' : 'Get a Quote'}
                                </button>
                                <button
                                    type="button"
                                    className={classes.ctaSecondary}
                                    onClick={() => openRequest('inspection')}
                                    aria-expanded={inquiryMode === 'inspection'}
                                >
                                    Request Inspection
                                </button>
                            </>
                        )}
                    </div>
                    <p className={classes.ctaNote}>
                        Dealer stock is available for immediate purchase. Ask a question or reserve it here - every request is saved to your garage.
                    </p>
                </div>
            </div>

            {/* ---------------- Get a Quote ---------------- */}
            {showQuote && !isCurrentInventory && (
                <section className={classes.quoteSection}>
                    <h2>Get a Quote</h2>
                    <p className={classes.quoteIntro}>
                        Price this {shortName || 'vehicle'} shipped to a US port, or fully imported and
                        customs-cleared.
                    </p>

                    <div className={classes.quoteControls}>
                        <div className={classes.quoteField}>
                            <label htmlFor="ds-port">Port</label>
                            {/* The port does not move the number — freight is priced
                                off body type — but it tells the team where to route
                                the car, so it rides along with the request. */}
                            <select id="ds-port" value={port} onChange={(e) => setPort(e.target.value)}>
                                <option value="">Select a port</option>
                                {US_PORTS.map((p) => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>

                        <fieldset className={classes.quoteField}>
                            <legend>Quote Type</legend>
                            <div className={classes.radioRow}>
                                <label className={classes.radio}>
                                    <input
                                        type="radio"
                                        name="quoteType"
                                        value="shipped"
                                        checked={quoteType === 'shipped'}
                                        onChange={() => {
                                            setQuoteType('shipped');
                                            // Nothing to title until it clears customs.
                                            setIncludeTitle(false);
                                        }}
                                    />
                                    <span>Shipped Price</span>
                                </label>
                                <label className={classes.radio}>
                                    <input
                                        type="radio"
                                        name="quoteType"
                                        value="imported"
                                        checked={quoteType === 'imported'}
                                        onChange={() => setQuoteType('imported')}
                                    />
                                    <span>Imported Price</span>
                                </label>
                            </div>
                        </fieldset>
                    </div>

                    <label
                        className={`${classes.titleOption} ${quoteType !== 'imported' ? classes.titleDisabled : ''}`}
                    >
                        <input
                            type="checkbox"
                            checked={includeTitle}
                            disabled={quoteType !== 'imported'}
                            onChange={(e) => setIncludeTitle(e.target.checked)}
                        />
                        <span>
                            Would you like us to title the vehicle for you? (+{formatUsd(TITLE_FEE)} for
                            US Title Service)
                            {quoteType !== 'imported' && (
                                <em className={classes.titleHint}> (Available only with imported price)</em>
                            )}
                        </span>
                    </label>

                    <div className={classes.quoteTotal}>
                        <span className={classes.quoteTotalLabel}>Estimated Total Price:</span>
                        <div className={classes.quoteTotalValue}>
                            {quote.total ? formatUsd(quote.total) : 'ASK'}
                        </div>
                        {quoteType === 'imported' && (
                            <p className={classes.quoteNote}>
                                This includes duty tax. Beforward does not include this in their clearing quote.
                            </p>
                        )}
                        {includeTitle && quoteType === 'imported' && (
                            <p className={classes.quoteNote}>US title service fee included.</p>
                        )}
                    </div>

                    <button
                        type="button"
                        className={classes.cta}
                        onClick={() => setInquiryMode('quote')}
                        aria-expanded={inquiryMode === 'quote'}
                    >
                        Request This Quote
                    </button>
                    <p className={classes.ctaNote}>
                        Estimate only. Final figures are confirmed by our team before any payment.
                    </p>
                </section>
            )}

            {/* ---------------- Inquiry / quote / inspection request ---------------- */}
            {inquiryMode && (
                <div ref={inquiryRef}>
                    <DealerInquiryForm
                        vehicle={vehicle}
                        type={inquiryMode}
                        quote={inquiryMode === 'quote' ? {
                            quoteType,
                            port,
                            includeTitle: quoteType === 'imported' && includeTitle,
                            estimatedTotal: quote.total || null,
                        } : null}
                        onClose={() => setInquiryMode(null)}
                    />
                </div>
            )}

            {/* ---------------- Description ---------------- */}
            <section className={classes.descriptionSection}>
                <h2>Vehicle Description</h2>
                {equipmentList ? (
                    <ul className={classes.descriptionList}>
                        {equipmentList.map((line, i) => (
                            <li key={`${line}-${i}`}>{line}</li>
                        ))}
                    </ul>
                ) : (
                    <p>{description || 'No detailed description available for this vehicle.'}</p>
                )}
            </section>

            {/* ---------------- Similar vehicles ---------------- */}
            <section className={classes.similarSection}>
                <h2>Similar Vehicles</h2>
                {similarLoading ? (
                    <div className={classes.stateContainer}>
                        <Loading text="Similar Vehicles" />
                    </div>
                ) : similar.length === 0 ? (
                    <p className={classes.similarEmpty}>
                        No similar vehicles in stock right now.{' '}
                        <Link to="/dealer-stock">Browse all dealer stock</Link>.
                    </p>
                ) : (
                    <div className={classes.similarGrid}>
                        {similar.map((car) => (
                            <DealerStockCard key={car._id} car={car} />
                        ))}
                    </div>
                )}
            </section>

            {/* ---------------- FAQ ---------------- */}
            <section className={classes.faqSection}>
                <h2>Import Questions</h2>
                <div className={classes.faqList}>
                    {faqs.map((faq, i) => (
                        <div key={faq.q} className={classes.faqItem}>
                            <button
                                type="button"
                                className={classes.faqQuestion}
                                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                aria-expanded={openFaq === i}
                            >
                                {faq.q}
                                <i
                                    className={`fas fa-chevron-${openFaq === i ? 'up' : 'down'}`}
                                    aria-hidden="true"
                                ></i>
                            </button>
                            {openFaq === i && <p className={classes.faqAnswer}>{faq.a}</p>}
                        </div>
                    ))}
                </div>
            </section>

            <div className={classes.footerNav}>
                <Link to="/dealer-stock" className={classes.backLink}>
                    <i className="fas fa-arrow-left" aria-hidden="true"></i> Back to Dealer Stock
                </Link>
            </div>

            {/* ---------------- Lightbox ---------------- */}
            {lightboxOpen && (
                <div
                    className={classes.lightbox}
                    ref={lightboxRef}
                    onClick={() => setLightboxOpen(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${title} image gallery`}
                >
                    <button
                        type="button"
                        className={classes.lightboxClose}
                        onClick={() => setLightboxOpen(false)}
                        aria-label="Close gallery"
                    >
                        <i className="fas fa-times" aria-hidden="true"></i>
                    </button>

                    <div className={classes.lightboxContent} onClick={(e) => e.stopPropagation()}>
                        <img
                            src={images[activeImage] || placeholderImage}
                            alt={`${title} ${activeImage + 1}`}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = placeholderImage;
                            }}
                        />

                        {images.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    className={`${classes.lightboxNav} ${classes.lightboxPrev}`}
                                    onClick={goToPrevious}
                                    aria-label="Previous image"
                                >
                                    <i className="fas fa-chevron-left" aria-hidden="true"></i>
                                </button>
                                <button
                                    type="button"
                                    className={`${classes.lightboxNav} ${classes.lightboxNext}`}
                                    onClick={goToNext}
                                    aria-label="Next image"
                                >
                                    <i className="fas fa-chevron-right" aria-hidden="true"></i>
                                </button>
                                <div className={classes.lightboxCounter}>
                                    {activeImage + 1} / {images.length}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DealerStockDetailContent;
